import { useEffect, useMemo, useState } from 'react'

import CustomAlert from '@ors/components/theme/Alerts/CustomAlert'
import Loading from '@ors/components/theme/Loading/Loading'
import CustomLink from '@ors/components/ui/Link/Link'
import useVisibilityChange from '@ors/hooks/useVisibilityChange'
import { useUpdatedFields } from '@ors/contexts/Projects/UpdatedFieldsContext'
import CancelWarningModal from '../ProjectSubmission/CancelWarningModal'
import ProjectTransfer from './ProjectTransfer'
import { CancelButton } from '../HelperComponents'
import { fetchSpecificFields } from '../hooks/getSpecificFields'
import { useGetProject } from '../hooks/useGetProject'
import { initialTranferedProjectData, MAX_FILE_SIZE } from '../constants'
import {
  FileMetaDataType,
  ProjectFilesObject,
  ProjectSpecificFields,
  ProjectTransferData,
  ProjectTypeApi,
} from '../interfaces'
import {
  getFormattedDecimalValue,
  getNonFieldErrors,
  getTransferErrors,
} from '../utils'
import { formatApiUrl, uploadFiles } from '@ors/helpers'

import { Modal, Typography, Box, CircularProgress } from '@mui/material'
import { debounce, fromPairs, keys, map, values } from 'lodash'
import { enqueueSnackbar } from 'notistack'
import Cookies from 'js-cookie'

const ProjectTransferWrapper = ({
  project,
  setIsModalOpen,
  onSuccess,
}: {
  project: ProjectTypeApi
  setIsModalOpen: (isOpen: boolean) => void
  onSuccess: (id: number) => void
}) => {
  const { updatedFields, addUpdatedField, clearUpdatedFields } =
    useUpdatedFields()

  useEffect(() => {
    clearUpdatedFields()
  }, [])

  const [projectData, setProjectData] = useState<ProjectTransferData>(
    initialTranferedProjectData,
  )
  const [files, setFiles] = useState<ProjectFilesObject>({
    deletedFilesIds: [],
    newFiles: [],
  })
  const [filesMetaData, setFilesMetaData] = useState<FileMetaDataType[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)

  const [errors, setErrors] = useState<{ [key: string]: [] }>({})
  const [fileErrors, setFileErrors] = useState<string>('')
  const [otherErrors, setOtherErrors] = useState<string>('')

  const nonFieldsErrors = getNonFieldErrors(errors)

  const { cluster_id, project_type_id, sector_id } = project
  const [_, setSpecificFields] = useState<ProjectSpecificFields[]>([])
  const [__, setSpecificFieldsLoaded] = useState<boolean>(false)
  const [shouldValidateTotalFund, setShouldValidateTotalFund] = useState(true)

  const debouncedFetchProjectFields = debounce(
    () =>
      fetchSpecificFields(
        cluster_id,
        project_type_id,
        sector_id,
        setSpecificFields,
        null,
        setSpecificFieldsLoaded,
        setShouldValidateTotalFund,
      ),
    0,
  )

  useEffect(() => {
    if (!!cluster_id && !!project_type_id && !!sector_id) {
      debouncedFetchProjectFields()
    } else {
      setSpecificFields([])
      setSpecificFieldsLoaded(true)
      setShouldValidateTotalFund(true)
    }
  }, [])

  const transferErrors = useMemo(
    () => getTransferErrors(projectData, project, shouldValidateTotalFund),
    [projectData, shouldValidateTotalFund],
  )

  const allFileErrors = [
    ...(fileErrors ? [{ message: fileErrors }] : []),
    ...(files.newFiles?.length === 0
      ? [{ message: 'At least one file must be attached.' }]
      : []),
  ]
  const missingFileTypeErrors = map(filesMetaData, ({ type, size }, index) => {
    const typeErrorMessage = !type ? 'Type is required.' : ''
    const sizeErrorMessage =
      (size ?? 0) > MAX_FILE_SIZE ? 'File size exceeds 20 MB.' : ''

    return typeErrorMessage || sizeErrorMessage
      ? {
          id: index,
          message: `Attachment ${Number(index) + 1} - ${typeErrorMessage} ${sizeErrorMessage}`,
        }
      : null
  }).filter(Boolean)

  const filteredErrors = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(errors).filter(([key]) =>
          keys(initialTranferedProjectData).includes(key),
        ),
      ),
    [errors],
  )
  const allTransferErrors = { ...transferErrors, ...filteredErrors }

  const disableTransfer =
    Object.values(transferErrors).some((errors: any) => errors.length > 0) ||
    missingFileTypeErrors.length > 0 ||
    files.newFiles?.length === 0

  useEffect(() => {
    setProjectData((prevData) => ({
      ...prevData,
      fund_transferred: getFormattedDecimalValue(project.total_fund),
      psc_transferred: getFormattedDecimalValue(project.support_cost_psc),
      psc_received: getFormattedDecimalValue(project.support_cost_psc),
    }))
  }, [])

  const setProjectDataWithEditTracking = (
    updater: React.SetStateAction<ProjectTransferData>,
    fieldName?: string,
  ) => {
    setProjectData((prevData) => {
      if (fieldName) {
        addUpdatedField(fieldName)
      }

      return typeof updater === 'function'
        ? (updater as (prev: ProjectTransferData) => ProjectTransferData)(
            prevData,
          )
        : updater
    })
  }

  const handleErrors = async (error: any) => {
    let errors: any = {}

    if (error instanceof Response) {
      const contentType = error.headers.get('content-type') || ''

      if (contentType.includes('application/json')) {
        errors = await error.json()
      } else {
        if (error.status === 413) {
          setFileErrors(
            'Uploaded files are too large. Maximum file size allowed is 170MB.',
          )
        }
        enqueueSnackbar(<>An error occurred. Please try again.</>, {
          variant: 'error',
        })

        return
      }

      setErrors(errors)

      if (errors?.files ?? errors?.file ?? errors?.metadata) {
        setFileErrors(
          [errors?.files, errors?.file, errors?.metadata]
            .filter(Boolean)
            .join('\n'),
        )
      }

      const otherError = errors?.details || errors?.detail
      if (otherError) {
        setOtherErrors(otherError)
      }
    }

    enqueueSnackbar(<>An error occurred. Please try again.</>, {
      variant: 'error',
    })
  }

  const transferProject = async () => {
    setIsLoading(true)
    setFileErrors('')
    setOtherErrors('')
    setErrors({})

    const filesForUpload = files.newFiles ?? []
    const formattedFilesMetadata = fromPairs(
      map(filesMetaData, (file) => [file.name, file.type]),
    )
    const params = { metadata: JSON.stringify(formattedFilesMetadata) }

    try {
      const formData = new FormData()

      filesForUpload.forEach((file) => {
        formData.append('files', file)
      })
      formData.append(keys(params)[0], values(params)[0])

      if (filesForUpload.length > 0) {
        await uploadFiles(
          `/api/project/files/validate/`,
          filesForUpload,
          false,
          'list',
          params,
        )
      }

      Object.entries(projectData).forEach(([key, value]) => {
        formData.append(key, value)
      })

      const csrftoken = Cookies.get('csrftoken')

      const response = await fetch(
        formatApiUrl(`/api/projects/v2/${project.id}/transfer`),
        {
          body: formData,
          credentials: 'include',
          headers: {
            ...(csrftoken ? { 'X-CSRFToken': csrftoken } : {}),
          },
          method: 'POST',
        },
      )
      const data = await response.json().catch(() => null)

      if (!response.ok) {
        throw data ?? { message: 'An error occurred' }
      }

      setIsModalOpen(false)
      enqueueSnackbar(<>Project transferred successfully.</>, {
        variant: 'success',
      })
      clearUpdatedFields()
      onSuccess(data.id)
    } catch (error) {
      await handleErrors(error)
    } finally {
      setIsLoading(false)
    }
  }

  const onCancel = () => {
    if (updatedFields.size > 0) {
      setIsCancelModalOpen(true)
    } else {
      setIsModalOpen(false)
    }
  }

  useVisibilityChange(updatedFields.size > 0)

  return (
    <>
      <ProjectTransfer
        {...{
          projectData,
          filesMetaData,
          setFilesMetaData,
          project,
          files,
          setFiles,
          missingFileTypeErrors,
          allFileErrors,
        }}
        setProjectData={setProjectDataWithEditTracking}
        errors={allTransferErrors}
      />
      {(nonFieldsErrors.length > 0 || otherErrors) && (
        <CustomAlert
          type="error"
          alertClassName="BPAlert mt-4"
          content={
            <div className="mt-0.5 text-lg">
              {otherErrors}
              {map(nonFieldsErrors, (err, idx) => (
                <div key={idx}>{err}</div>
              ))}
            </div>
          }
        />
      )}
      <div className="mr-6 mt-auto flex flex-wrap justify-end gap-3">
        <CustomLink
          className="px-4 py-2 text-lg uppercase"
          onClick={transferProject}
          disabled={disableTransfer}
          href={null}
          color="secondary"
          variant="contained"
          button
        >
          Transfer project
        </CustomLink>
        <CancelButton onClick={onCancel} />
        {isLoading && (
          <CircularProgress
            color="inherit"
            size="30px"
            className="text-align mb-1 ml-1.5 mt-auto"
          />
        )}
      </div>
      {isCancelModalOpen && (
        <CancelWarningModal
          mode="project transfer"
          isModalOpen={isCancelModalOpen}
          setIsModalOpen={setIsCancelModalOpen}
          onContinueAction={() => setIsModalOpen(false)}
        />
      )}
    </>
  )
}

const TransferProjectModal = ({
  id,
  isModalOpen,
  setIsModalOpen,
  onSuccess,
}: {
  id: number
  isModalOpen: boolean
  setIsModalOpen: (isOpen: boolean) => void
  onSuccess: (id: number) => void
}) => {
  const project = useGetProject(id.toString())
  const { data, loading } = project
  const projectCode = data?.code ?? data?.code_legacy

  return (
    <Modal aria-labelledby="transfer-modal" open={isModalOpen} keepMounted>
      <Box className="flex max-h-[95%] min-h-[250px] w-[80%] max-w-[1400px] flex-col rounded-2xl border-0 p-0 absolute-center 2xl:w-[60%]">
        <Typography className="mb-1 flex flex-wrap gap-2 rounded-t-2xl bg-primary px-6 py-2 text-3xl font-medium text-white">
          Transfer project
          <div
            className="overflow-hidden text-ellipsis whitespace-nowrap"
            title={projectCode}
          >
            {projectCode}
          </div>
        </Typography>
        <Loading
          className="!fixed bg-action-disabledBackground"
          active={loading}
        />
        <div className="flex flex-col overflow-y-auto px-6 py-3">
          {!loading && data && (
            <ProjectTransferWrapper
              project={data}
              {...{ setIsModalOpen, onSuccess }}
            />
          )}
        </div>
      </Box>
    </Modal>
  )
}

export default TransferProjectModal
